/**
 * PupRing Custom JavaScript - Cart Module
 * ========================================
 * Handles cart drawer enhancements and functionality
 *
 * Features:
 * - CartEnhancementManager class
 * - Variant show/hide toggle
 * - Currency conversion for free shipping
 * - Dynamic shipping threshold updates
 * - Gift box and warranty card click handlers
 *
 * Dependencies: bootstrap (external)
 */

/**
 * CartEnhancementManager - Manages cart drawer enhancements
 */
class CartEnhancementManager {
  constructor() {
    this.initialized = false;
    this.cartObserver = null;
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!window._cartEnhancementManager) {
      window._cartEnhancementManager = new CartEnhancementManager();
    }
    return window._cartEnhancementManager;
  }

  /**
   * Initialize cart enhancements
   */
  async init() {
    if (this.initialized) return;
    this.initialized = true;

    // CRITICAL FIX: Fetch cart data on page load BEFORE any calculations
    await this.fetchAndUpdateCartData();

    this.initVariantToggle();
    this.initCurrencyConversion();
    this.observeCartChanges();
    this.attachEventListeners();
  }

  /**
   * Initialize variant show/hide toggle functionality
   * Adapted for Ecomify theme structure
   */
  initVariantToggle() {
    // Target each cart product item
    const cartItems = document.querySelectorAll('.cart-product-item');

    cartItems.forEach(item => {
      const variantContainer = item.querySelector('.cart-item-variants');
      if (!variantContainer) return;

      // Get direct children elements to hide/show
      // 1. variant title <small> (immediate child) - ALWAYS HIDE
      // 2. selling plan <small> (immediate child)
      // 3. property <li> elements (inside <ul>)
      const variantTitle = variantContainer.querySelector('.variant-title');
      const otherSmallElements = Array.from(variantContainer.querySelectorAll(':scope > small')).filter(el => !el.classList.contains('variant-title'));
      const propertyList = variantContainer.querySelector('.list-unstyled');
      const propertyItems = propertyList ? Array.from(propertyList.querySelectorAll('li')) : [];

      // Combine other elements (not variant title)
      const otherElements = [...otherSmallElements, ...propertyItems];

      // Track hidden elements
      const hiddenElements = [];

      // Always hide variant title
      if (variantTitle) {
        variantTitle.classList.add('cart-variant-hidden');
        hiddenElements.push(variantTitle);
      }

      // Hide other elements beyond the 2nd (keep first 2 visible)
      otherElements.forEach((elem, index) => {
        if (index > 1) {  // Changed from >= 2 to > 1 for clarity
          elem.classList.add('cart-variant-hidden');
          hiddenElements.push(elem);
        }
      });

      // Only add toggle if we have hidden elements
      if (hiddenElements.length === 0) return;

      // Check if toggle already exists
      if (variantContainer.querySelector('.cart-variant-toggle')) return;

      // Create toggle button
      const toggleBtn = document.createElement('button');
      toggleBtn.className = 'cart-variant-toggle';
      toggleBtn.textContent = 'Show more';
      toggleBtn.setAttribute('type', 'button');
      toggleBtn.style.cssText = `
        color: #666;
        font-size: 11px;
        font-weight: 400;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
        border: none;
        background: none;
        margin-top: 2px;
        display: block;
      `;

      // Fixed toggle logic with proper expand/collapse
      toggleBtn.onclick = () => {
        const isExpanded = toggleBtn.classList.contains('expanded');

        if (isExpanded) {
          // Collapse - hide the elements that should be hidden
          hiddenElements.forEach(elem => {
            elem.classList.add('cart-variant-hidden');
          });
          toggleBtn.classList.remove('expanded');
          toggleBtn.textContent = 'Show more';
        } else {
          // Expand - show all hidden elements
          hiddenElements.forEach(elem => {
            elem.classList.remove('cart-variant-hidden');
          });
          toggleBtn.classList.add('expanded');
          toggleBtn.textContent = 'Show less';
        }
      };

      variantContainer.appendChild(toggleBtn);
    });
  }

  /**
   * Initialize currency conversion for free shipping bar
   */
  initCurrencyConversion() {
    this.updateShippingThreshold();
  }

  /**
   * Update shipping threshold based on cart total and settings
   */
  updateShippingThreshold(cartData = null) {
    // Get configuration from data attributes
    const config = document.getElementById('cart-shipping-config');
    if (!config) return;

    const thresholdCents = parseInt(config.dataset.thresholdCents) || 6000; // Default $60
    const shippingCostCents = parseInt(config.dataset.shippingCost) || 695; // Default to $6.95
    const currencyCode = config.dataset.thresholdCurrency || 'USD';

    // Get cart total in cents (use passed data, window data, or DOM)
    let cartTotalCents = 0;
    const cart = cartData || window.cartData;

    if (cart && cart.total_price !== undefined) {
      cartTotalCents = cart.total_price;
    } else {
      // Fallback: parse from displayed subtotal with correct selector
      const subtotalElement = document.querySelector('[data-subtotal]');
      if (subtotalElement) {
        // Better parsing to handle currency formatting
        const subtotalText = subtotalElement.textContent.trim();
        const subtotalMatch = subtotalText.match(/[\d,]+\.?\d*/);
        if (subtotalMatch) {
          cartTotalCents = Math.round(parseFloat(subtotalMatch[0].replace(',', '')) * 100);
        }
      }
    }

    // Update ALL shipping text elements (handle multiple locations)
    const shippingTexts = document.querySelectorAll('[data-cart-shipping-calculator]');
    shippingTexts.forEach(shippingText => {
      if (cartTotalCents >= thresholdCents) {
        // Only update if not already showing FREE
        if (!shippingText.innerHTML.includes('FREE')) {
          shippingText.innerHTML = '<strong>FREE</strong>';
        }
        shippingText.classList.add('text-primary');
      } else {
        // Format shipping cost
        const shippingFormatted = (shippingCostCents / 100).toFixed(2);
        // Only update if different
        if (!shippingText.innerHTML.includes(shippingFormatted)) {
          shippingText.innerHTML = `$${shippingFormatted}`;
        }
        shippingText.classList.remove('text-primary');
      }
    });

    // Also update progress bar if exists (for free shipping goal)
    const progressBar = document.querySelector('#progress-bar');
    const messageElement = document.querySelector('#shipping-message');

    if (progressBar && messageElement) {
      if (cartTotalCents >= thresholdCents) {
        messageElement.textContent = "You've unlocked FREE Shipping!";
        progressBar.style.width = '100%';
        progressBar.classList.add('free-shipping-unlocked');
      } else {
        const remainingCents = thresholdCents - cartTotalCents;
        const progress = Math.min((cartTotalCents / thresholdCents) * 100, 100);

        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });

        const remainingDollars = remainingCents / 100;
        messageElement.textContent = `Unlock FREE Shipping with ${formatter.format(remainingDollars)} More!`;
        progressBar.style.width = `${progress}%`;
        progressBar.classList.remove('free-shipping-unlocked');
      }
    }
  }

  /**
   * Fetch and update cart data from Shopify API
   */
  async fetchAndUpdateCartData() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      window.cartData = cartData; // Store globally for other functions
      return cartData;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current cart total
   */
  getCartTotal() {
    // First try to get from window.cartData
    if (window.cartData && window.cartData.total_price !== undefined) {
      return window.cartData.total_price / 100; // Convert cents to dollars
    }

    // Fallback to DOM parsing with correct selector
    const subtotalElement = document.querySelector('[data-subtotal]');
    if (!subtotalElement) return 0;

    // Extract numeric value from money string
    const moneyString = subtotalElement.textContent;
    const numericValue = parseFloat(moneyString.replace(/[^0-9.-]+/g, ''));

    // Convert cents to dollars if needed
    return numericValue > 1000 ? numericValue / 100 : numericValue;
  }

  /**
   * Format money based on currency
   */
  formatMoney(amount) {
    const currencyCode = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback to basic formatting
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Observe cart DOM changes and re-initialize features
   */
  observeCartChanges() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }

    const cartBody = document.querySelector('#offcanvas-cart .offcanvas-body');
    if (!cartBody) {
      // Retry if cart not loaded yet
      setTimeout(() => this.observeCartChanges(), 500);
      return;
    }

    this.cartObserver = new MutationObserver((mutations) => {
      let shouldReinit = false;

      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 &&
                (node.classList?.contains('cart-product-item') ||
                 node.querySelector?.('.cart-product-item'))) {
              shouldReinit = true;
            }
          });
        }
      });

      if (shouldReinit) {
        // Debounce re-initialization
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
          this.initVariantToggle();
          this.updateShippingThreshold();
        }, 200);
      }
    });

    this.cartObserver.observe(cartBody, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Attach event listeners for cart updates
   */
  attachEventListeners() {
    // Listen for cart updates
    window.addEventListener('updated.ecomify.cart', async (e) => {
      // Step 1: Wait for DOM to stabilize
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 2: Use cart data from event or fetch if not provided
      const cartData = e.detail?.cart || await this.fetchAndUpdateCartData();

      // Step 3: Update UI elements with cart data
      if (cartData) {
        this.updateShippingThreshold(cartData);
      }

      // Step 4: Reinitialize variant toggles
      this.initVariantToggle();
    });

    // Also listen for cart:updated event (old theme compatibility)
    document.addEventListener('cart:updated', async () => {
      // Same logic as above
      await new Promise(resolve => setTimeout(resolve, 200));
      const cartData = await this.fetchAndUpdateCartData();
      if (cartData) {
        this.updateShippingThreshold(cartData);
      }
      this.initVariantToggle();
    });

    // Re-initialize when cart drawer opens
    const cartOffcanvas = document.getElementById('offcanvas-cart');
    if (cartOffcanvas) {
      cartOffcanvas.addEventListener('shown.bs.offcanvas', () => {
        this.initVariantToggle();
        this.updateShippingThreshold();

        // Check if BOGO modal should be shown
        if (typeof BOGOUtilsAdapter !== 'undefined') {
          const bogoAdapter = BOGOUtilsAdapter.getInstance();
          // Fetch fresh cart data and update step
          bogoAdapter.fetchCartData().then(() => {
            bogoAdapter.updateProgressBar();
            // Check if we're at Step 2 (not just count === 1)
            if (bogoAdapter.currentStep === 2) {
              bogoAdapter.checkAndShowBOGOModal();
            }
          });
        }
      });
    }

    // Listen for currency changes
    document.addEventListener('currency:changed', () => {
      this.updateShippingThreshold();
    });
  }

  /**
   * Cleanup
   */
  cleanup() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
    clearTimeout(this.updateTimeout);
    this.initialized = false;
  }
}

// Initialize cart enhancements
async function initCartEnhancements() {
  // CRITICAL: Await async init to ensure cart data is fetched on page load
  const manager = CartEnhancementManager.getInstance();
  await manager.init();

  // Expose to window for cart.js to access
  window.CartEnhancementManager = manager;

  // Make entire gift box clickable for better mobile UX
  initGiftBoxClickHandlers();
}

// Make gift box cards fully clickable
function initGiftBoxClickHandlers() {
  // Remove any existing listeners to prevent duplicates
  if (window.giftBoxClickHandler) {
    document.removeEventListener('click', window.giftBoxClickHandler);
  }

  // Create the click handler
  window.giftBoxClickHandler = function(e) {
    // Check if clicked element is within a gift box item
    const giftBox = e.target.closest('.gift-box-item');
    if (giftBox) {
      // Don't trigger if clicking directly on the add button
      if (e.target.closest('.gift-box-add')) return;

      // Find and click the add button within this gift box
      const addButton = giftBox.querySelector('.gift-box-add');
      if (addButton && !addButton.disabled) {
        // Provide visual feedback
        giftBox.style.opacity = '0.7';
        setTimeout(() => {
          giftBox.style.opacity = '';
        }, 150);

        // Click the button
        addButton.click();
      }
      return;
    }

    // Check if clicked element is within a warranty card
    const warrantyCard = e.target.closest('.warranty-card');
    if (warrantyCard) {
      // Don't trigger if clicking directly on the add button
      if (e.target.closest('.warranty-add')) return;

      // Find and click the add button within this warranty card
      const addButton = warrantyCard.querySelector('.warranty-add');
      if (addButton && !addButton.disabled) {
        // Provide visual feedback
        warrantyCard.style.opacity = '0.7';
        setTimeout(() => {
          warrantyCard.style.opacity = '';
        }, 150);

        // Click the button
        addButton.click();
      }
    }
  };

  // Add the event listener
  document.addEventListener('click', window.giftBoxClickHandler);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartEnhancements);
} else {
  initCartEnhancements();
}

// Re-initialize on Shopify events
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#offcanvas-cart')) {
    initCartEnhancements();
  }
});
